import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import AFL_PLAYERS from '../../../assets/static_data/afl_players.json';
import propToolService from '../../../utils/services/propToolService';
import { getTeamMeta } from '../../SnipeHub/aflTeams';
import PropStatBarChart from './PropStatBarChart';
import ImpliedEdgeTable from './ImpliedEdgeTable';
import CustomPropDataFilterModal from './CustomPropDataFilterModal';
import SubscriptionRequired from '../../common/SubscriptionRequired';

// Maps each market chip to the matching stat field on the API stat rows.
const STAT_FIELD = {
    disposals: 'disposals',
    goals: 'goals',
    marks: 'marks',
    tackles: 'tackles',
    kicks: 'kicks',
    handballs: 'handballs',
    clearances: 'clearances',
    hit_outs: 'hit_outs',
};

const MARKET_OPTIONS = [
    { value: 'disposals', label: 'Disposals' },
    { value: 'goals', label: 'Goals' },
    { value: 'marks', label: 'Marks' },
    { value: 'tackles', label: 'Tackles' },
    { value: 'kicks', label: 'Kicks' },
    { value: 'handballs', label: 'Handballs' },
    { value: 'clearances', label: 'Clearances' },
    { value: 'hit_outs', label: 'Hit Outs' },
];

// Game-log numeric columns, in display order. `field` reads straight off the
// stat row; `get` derives a value. Percentage columns are already 0–100.
const GAME_LOG_STAT_COLS = [
    { field: 'disposals', label: 'Disposals' },
    { field: 'kicks', label: 'Kicks' },
    { field: 'handballs', label: 'Handballs' },
    { field: 'marks', label: 'Marks' },
    { field: 'tackles', label: 'Tackles' },
    { field: 'goals', label: 'Goals' },
    { field: 'behinds', label: 'Behinds' },
    { field: 'clearances', label: 'Clearances' },
    { field: 'fantasy_points', label: 'Fantasy Points' },
    { field: 'hit_outs', label: 'Hit Outs' },
    { field: 'cba', label: 'CBA %' },
    { field: 'ruck_contests', label: 'Ruck Contests %' },
    { field: 'kick_ins', label: 'Kick Ins' },
    { field: 'tog', label: 'TOG %' },
];

const MAX_RESULTS = 8;

const TIMEFRAME_OPTIONS = [
    { value: 'l5', label: 'Last 5', short: 'L5' },
    { value: 'l10', label: 'Last 10', short: 'L10' },
    { value: 'l20', label: 'Last 20', short: 'L20' },
];

// Number of most-recent games a dataset window covers.
const TIMEFRAME_SIZE = { l5: 5, l10: 10, l20: 20 };

// Seasons spanned by the last N games (chronological stats assumed), most
// recent first — used to keep the season chip in sync with the dataset window.
function seasonsInWindow(stats, tf) {
    const n = TIMEFRAME_SIZE[tf] || 0;
    if (!n || !stats?.length) return [];
    const last = stats.slice(-n);
    return Array.from(new Set(last.map((s) => s.season))).filter((v) => v != null).sort((a, b) => b - a);
}

// Coerce an API stat field to a number, treating null/undefined as missing.
function num(v) {
    return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}

// Mean of the non-null numeric values in a list (null when there's nothing to average).
function average(values) {
    const nums = values.filter((v) => v != null);
    if (!nums.length) return null;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
}

// Designated home/away for this game (who hosted), normalised through full club
// names so abbreviation mismatches don't flip the result. Used only to label the
// tooltip's vs / @.
function designatedSide(stat) {
    if (!stat?.game?.home_team || !stat.team) return null;
    const playerFull = getTeamMeta(stat.team).full;
    const homeFull = getTeamMeta(stat.game.home_team).full;
    const awayFull = getTeamMeta(stat.game.away_team).full;
    if (playerFull && homeFull && playerFull === homeFull) return 'home';
    if (playerFull && awayFull && playerFull === awayFull) return 'away';
    const homeAbbr = getTeamMeta(stat.game.home_team).abbr;
    if (!homeAbbr) return null;
    return stat.team.toUpperCase() === homeAbbr.toUpperCase() ? 'home' : 'away';
}

// Resolve the opponent's full club name from the game object — the same full
// names SnipeHub renders guernseys from. The opponent is whichever of
// home_team / away_team is NOT the player's club. We normalise every side
// through getTeamMeta (which resolves both full names and abbreviations) so any
// abbreviation/format mismatch can't make this fall through to a bare code.
function resolveOpponentFull(s) {
    const home = s?.game?.home_team;
    const away = s?.game?.away_team;
    if (!home || !away) return getTeamMeta(s?.opponent).full || s?.opponent || '';

    const playerFull = getTeamMeta(s?.team).full;
    const homeFull = getTeamMeta(home).full;
    const awayFull = getTeamMeta(away).full;

    // Primary: pick the side that isn't the player's club.
    if (playerFull && playerFull === homeFull) return away;
    if (playerFull && playerFull === awayFull) return home;

    // Fallback: match the recorded opponent to a side.
    const oppFull = getTeamMeta(s?.opponent).full;
    if (oppFull && oppFull === homeFull) return home;
    if (oppFull && oppFull === awayFull) return away;

    // Last resort: return a raw game side (a real club name) so a jersey always
    // renders, rather than a derived abbreviation that has no guernsey recipe.
    return playerFull && playerFull === awayFull ? home : away;
}

// Short round label for chart axes — "R5" for numeric rounds, finals codes as-is.
function roundShort(stat) {
    const r = String(stat.round ?? stat.game?.round ?? '');
    if (!r) return '';
    return /^\d+$/.test(r) ? `R${r}` : r;
}

function formatGameDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

// A sensible default over/under line for a given average (lands on a .5).
function defaultLineFor(avg) {
    if (avg == null) return '';
    return String(Math.max(0.5, Math.round(avg) - 0.5));
}

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Player avatar with graceful fallback to initials when the remote image fails.
function PlayerAvatar({ player, size = 40, ring = true }) {
    const [errored, setErrored] = useState(false);
    useEffect(() => { setErrored(false); }, [player?.player_id]);

    const dim = { width: size, height: size };
    const ringCls = ring ? 'ring-1 ring-slate-700/60' : '';

    if (!player) {
        return (
            <div
                style={dim}
                className={`rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 ${ringCls}`}
            >
                <svg className="w-1/2 h-1/2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            </div>
        );
    }

    if (errored || !player.image_src) {
        return (
            <div
                style={dim}
                className={`rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center flex-shrink-0 ${ringCls}`}
            >
                <span className="font-mono font-bold text-emerald-300/90" style={{ fontSize: size * 0.34 }}>
                    {getInitials(player.player_name)}
                </span>
            </div>
        );
    }

    return (
        <div
            style={dim}
            className={`rounded-full bg-slate-800 overflow-hidden flex-shrink-0 ${ringCls}`}
        >
            <img
                src={player.image_src}
                alt={player.player_name}
                width={size}
                height={size}
                loading="lazy"
                onError={() => setErrored(true)}
                className="w-full h-full object-cover object-top"
            />
        </div>
    );
}

function SectionLabel({ children, right }) {
    return (
        <div className="flex items-center gap-2.5 mb-3">
            <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">{children}</span>
            <span className="h-px flex-1 bg-slate-800/70" />
            {right}
        </div>
    );
}

function FilterField({ label, children, className = '' }) {
    return (
        <div className={className}>
            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-500 mb-1.5">{label}</div>
            {children}
        </div>
    );
}

function Segmented({ options, value, onChange }) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => onChange(o.value)}
                        className={`flex-1 md:flex-none px-2.5 py-2 rounded-lg text-[11px] font-mono font-medium border transition-all ${
                            active
                                ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                        }`}
                    >
                        {o.short ? (
                            <>
                                <span className="md:hidden">{o.short}</span>
                                <span className="hidden md:inline">{o.label}</span>
                            </>
                        ) : o.label}
                    </button>
                );
            })}
        </div>
    );
}

// A chip-styled trigger that opens a popover menu. `children` is a render prop
// receiving `{ close }` so single-select menus can dismiss on pick while the
// multi-select (seasons) menu stays open. Closes on outside click.
function ChipDropdown({ summary, active, panelWidth = 'w-44', children }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={open}
                className={`flex w-full md:w-auto items-center justify-between md:justify-start gap-1.5 px-2.5 py-2 rounded-lg text-[11px] font-mono font-medium border transition-all ${
                    active
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                        : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                }`}
            >
                <span className="truncate md:max-w-[150px]">{summary}</span>
                <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className={`absolute z-50 left-0 mt-1.5 ${panelWidth} rounded-xl border border-slate-700/70 bg-slate-900 shadow-2xl shadow-black/60 overflow-hidden`}>
                    {children({ close: () => setOpen(false) })}
                </div>
            )}
        </div>
    );
}

// A single row inside a ChipDropdown menu (checkbox for multi, tick for single).
function ChipMenuItem({ label, checked, multi, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12px] transition-colors hover:bg-slate-800/60"
        >
            {multi ? (
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                    checked ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600'
                }`}>
                    {checked && (
                        <svg className="w-2.5 h-2.5 text-slate-950" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </span>
            ) : (
                <span className="w-3.5 flex-shrink-0">
                    {checked && (
                        <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </span>
            )}
            <span className={`flex-1 truncate font-mono ${checked ? 'text-white' : 'text-slate-300'}`}>{label}</span>
        </button>
    );
}

// Accent colour palettes for the teammate selectors (amber = without, sky = with).
const TEAMMATE_ACCENTS = {
    amber: { btn: 'bg-amber-500/15 text-amber-300 border-amber-500/40', box: 'bg-amber-500 border-amber-500' },
    sky: { btn: 'bg-sky-500/15 text-sky-300 border-sky-500/40', box: 'bg-sky-500 border-sky-500' },
};

// Teammate chip + searchable, multi-select teammate menu. Selecting players
// re-queries the API for the relevant teammate split (with / without).
function TeammateFilter({ teammates, selected, onToggle, summary, active, accent = 'amber', placeholder = 'Search teammate…' }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const ref = useRef(null);
    const palette = TEAMMATE_ACCENTS[accent] || TEAMMATE_ACCENTS.amber;

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        return t ? teammates.filter((p) => p.player_name.toLowerCase().includes(t)) : teammates;
    }, [teammates, q]);

    const isSel = (id) => selected.some((p) => p.player_id === id);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={open}
                className={`flex w-full md:w-auto items-center justify-between md:justify-start gap-1.5 px-2.5 py-2 rounded-lg text-[11px] font-mono font-medium border transition-all ${
                    active
                        ? palette.btn
                        : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                }`}
            >
                <span className="truncate md:max-w-[150px]">{summary}</span>
                <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className="absolute z-50 left-0 mt-1.5 w-64 rounded-xl border border-slate-700/70 bg-slate-900 shadow-2xl shadow-black/60 overflow-hidden">
                    <div className="p-2 border-b border-slate-800/80">
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={placeholder}
                            className="w-full rounded-md bg-slate-950/70 border border-slate-700/70 focus:border-emerald-500/50 text-[12px] text-white placeholder-slate-600 px-2.5 py-1.5 focus:outline-none"
                            autoComplete="off"
                            spellCheck="false"
                        />
                    </div>
                    <div className="py-1 max-h-[260px] overflow-y-auto styled-scrollbar">
                        {filtered.length ? filtered.map((p) => {
                            const checked = isSel(p.player_id);
                            return (
                                <button
                                    key={p.player_id}
                                    type="button"
                                    onClick={() => onToggle(p)}
                                    className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-slate-800/60"
                                >
                                    <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                                        checked ? palette.box : 'border-slate-600'
                                    }`}>
                                        {checked && (
                                            <svg className="w-2.5 h-2.5 text-slate-950" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </span>
                                    <PlayerAvatar player={p} size={22} ring={false} />
                                    <span className={`flex-1 truncate text-[12px] font-mono ${checked ? 'text-white' : 'text-slate-300'}`}>{p.player_name}</span>
                                </button>
                            );
                        }) : (
                            <div className="px-3 py-3 text-center text-[11px] font-mono text-slate-600">
                                {teammates.length ? 'No match' : 'Select a player first'}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function PropLoadingState() {
    return (
        <div className="flex flex-col items-center justify-center text-center py-16">
            <div className="w-10 h-10 rounded-full border-2 border-slate-700 border-t-emerald-400 animate-spin mb-4" />
            <p className="text-xs font-mono text-slate-500 tracking-wide">Loading historical data…</p>
        </div>
    );
}

function PropEmptyState({ title, message }) {
    return (
        <div className="flex flex-col items-center justify-center text-center py-14 px-6">
            <div className="w-12 h-12 rounded-2xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            </div>
            <h4 className="text-sm font-semibold text-white mb-1.5">{title}</h4>
            <p className="text-xs font-mono text-slate-500 leading-relaxed max-w-xs">{message}</p>
        </div>
    );
}

function PropErrorState({ error, onRetry }) {
    if (error?.isSubscriptionError) {
        return <SubscriptionRequired feature="the player prop tool" variant="page" />;
    }

    const errorConfig = {
        AUTH: { icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636', color: 'text-red-400', bg: 'bg-red-500/5 border-red-500/10', title: 'Authentication Failed' },
        PLAYER_NOT_FOUND: { icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', color: 'text-slate-400', bg: 'bg-slate-500/5 border-slate-500/10', title: 'Player Not Found' },
        RATE_LIMIT_EXCEEDED: { icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-amber-400', bg: 'bg-amber-500/5 border-amber-500/10', title: 'Too Many Requests' },
        NETWORK_ERROR: { icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-red-400', bg: 'bg-red-500/5 border-red-500/10', title: 'Connection Error' },
    };
    const code = error?.isAuthError ? 'AUTH' : (error?.errorCode || 'NETWORK_ERROR');
    const config = errorConfig[code] || errorConfig.NETWORK_ERROR;
    const showRetry = ['SERVER_ERROR', 'NETWORK_ERROR', 'SUBSCRIPTION_CHECK_FAILED'].includes(error?.errorCode);

    return (
        <div className="flex flex-col items-center text-center py-14 px-6 max-w-sm mx-auto">
            <div className={`w-12 h-12 rounded-lg ${config.bg} border flex items-center justify-center mb-4`}>
                <svg className={`w-6 h-6 ${config.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={config.icon} />
                </svg>
            </div>
            <h4 className={`text-sm font-semibold mb-1.5 ${config.color}`}>{config.title}</h4>
            <p className="text-xs font-mono text-slate-500 leading-relaxed">{error?.message || 'Something went wrong.'}</p>
            {showRetry && (
                <button
                    onClick={onRetry}
                    className="mt-5 px-4 py-2 text-xs font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all flex items-center gap-2"
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Retry
                </button>
            )}
        </div>
    );
}

function PropAnalysis({ playerName, marketKey, marketLabel, series, marketAvg, line, validLine, onLineChange, canTip, preferredBookmaker }) {
    const log = series.slice().reverse(); // most-recent first
    const cell = (v) => (v == null ? '—' : v);
    const datasetValues = series.map((g) => g.value).filter((v) => v != null);
    const activeField = STAT_FIELD[marketKey]; // highlight the active prop's column

    return (
        <div className="space-y-4">
            {/* Performance card */}
            <section className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-3 md:p-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <SectionLabel>{marketLabel} · Performance</SectionLabel>
                    <div className="flex items-center gap-2 -mt-3">
                        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">Line</span>
                        <input
                            type="number"
                            inputMode="decimal"
                            step="0.5"
                            min="0"
                            value={line}
                            onChange={(e) => onLineChange(e.target.value)}
                            placeholder="—"
                            className="w-20 rounded-lg bg-slate-950/70 border border-slate-700/70 focus:border-emerald-500/50 text-sm font-mono text-white text-center px-2 py-1.5 focus:outline-none transition-colors"
                        />
                    </div>
                </div>

                {/* Legend */}
                {(validLine != null || marketKey === 'goals') && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-2 text-[10px] font-mono text-slate-500">
                        {validLine != null && (
                            <span className="flex items-center gap-1.5"><span className="w-4 h-px border-t border-dashed border-slate-300" /> Line</span>
                        )}
                        {marketKey === 'goals' && (
                            <>
                                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Goals</span>
                                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border border-emerald-400/60" style={{ backgroundImage: 'repeating-linear-gradient(45deg,rgba(52,211,153,0.55) 0 2px,transparent 2px 4px)' }} /> Behinds (scoring shots)</span>
                            </>
                        )}
                    </div>
                )}

                <PropStatBarChart games={series} marketLabel={marketLabel} line={validLine} average={marketAvg} stackBehinds={marketKey === 'goals'} />

                {/* Implied edge vs bookmaker lines */}
                <ImpliedEdgeTable
                    playerName={playerName}
                    marketKey={marketKey}
                    marketLabel={marketLabel}
                    userLine={validLine}
                    datasetValues={datasetValues}
                    canTip={canTip}
                    preferredBookmaker={preferredBookmaker}
                    onSelectLine={(col) => {
                        // Bookmaker columns are "N+" thresholds; convert to the
                        // equivalent over/under .5 line so the chart hit set matches.
                        const v = col.isUser ? col.x : col.x - 0.5;
                        onLineChange(String(v));
                    }}
                />
            </section>

            {/* Game log */}
            <section className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-3 md:p-4">
                <SectionLabel>Game Log</SectionLabel>
                <div className="overflow-x-auto styled-scrollbar -mx-1">
                    <table className="w-full min-w-full text-xs whitespace-nowrap">
                        <thead>
                            <tr className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 border-b border-slate-800/80">
                                <th className="text-left font-medium py-2 px-2">Season</th>
                                <th className="text-left font-medium py-2 px-2">Round</th>
                                <th className="text-left font-medium py-2 px-2">Opponent</th>
                                <th className="text-left font-medium py-2 px-2">Venue</th>
                                {GAME_LOG_STAT_COLS.map((c) => {
                                    const active = c.field && c.field === activeField;
                                    return (
                                        <th key={c.key || c.field} className={`text-right font-medium py-2 px-2 ${active ? 'text-emerald-400/90' : ''}`}>
                                            {c.label}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {log.map((g) => {
                                const s = g.stat;
                                return (
                                    <tr key={g.key} className="border-b border-slate-800/40 hover:bg-slate-800/30 transition-colors">
                                        <td className="py-2 px-2 font-mono text-slate-400">{g.season ?? '—'}</td>
                                        <td className="py-2 px-2 font-mono text-slate-300">{g.roundShort || '—'}</td>
                                        <td className="py-2 px-2 font-mono text-slate-300">
                                            <span className="text-slate-600">{g.isHome ? 'vs' : '@'}</span> {g.opponent || '—'}
                                        </td>
                                        <td className="py-2 px-2 font-mono text-slate-400">{g.venue || '—'}</td>
                                        {GAME_LOG_STAT_COLS.map((c) => {
                                            const active = c.field && c.field === activeField;
                                            const value = c.get ? c.get(s) : num(s[c.field]);
                                            return (
                                                <td
                                                    key={c.key || c.field}
                                                    className={`py-2 px-2 text-right font-mono tabular-nums ${active ? 'text-emerald-300 font-semibold' : 'text-slate-400'}`}
                                                >
                                                    {cell(value)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

function AFLPropTool() {
    const navigate = useNavigate();
    const location = useLocation();
    const { hasRole } = useAuth();
    const canTip = hasRole('COMMUNITY_TIPPER');
    // Where the Back button returns to — the page that deep-linked us here
    // (e.g. the Snipe Feed), falling back to the Snipe Hub.
    const backTo = location.state?.from || '/snipe-hub';
    // Bookmaker the implied-edge table should default to when deep-linked.
    const deepLinkBook = location.state?.bookmaker
        ? String(location.state.bookmaker).toLowerCase()
        : null;
    const [query, setQuery] = useState('');
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [market, setMarket] = useState('disposals');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(0);

    // Data-transform filters. `timeframe` is null by default — no recency window,
    // so the full selected season is shown. L5/L10/L20 are optional windows.
    const [timeframe, setTimeframe] = useState(null);
    // Multi-select seasons. Empty array means "all seasons".
    const [selectedSeasons, setSelectedSeasons] = useState([]);
    const [venue, setVenue] = useState('all');
    const [opponent, setOpponent] = useState('all');
    // "Without" teammates — server-side filter (games where none of them played).
    // Stored as full player objects so we can render names/photos in the chip.
    const [withoutPlayers, setWithoutPlayers] = useState([]);
    // "With" teammates — inverse server-side filter (games where the player and
    // all of them played together). Same-team players, mutually exclusive with
    // the "without" selection so we never request a contradiction.
    const [withPlayers, setWithPlayers] = useState([]);
    // Custom hand-picked dataset (array of game ids) or null when not in use.
    // When active it overrides the season/venue/opponent/timeframe filters.
    const [customGameIds, setCustomGameIds] = useState(null);
    // Persisted filter settings (seasons + range filters) for the custom dataset,
    // so reopening the modal restores the user's last configuration.
    const [customSettings, setCustomSettings] = useState(null);
    const [showCustomModal, setShowCustomModal] = useState(false);
    const customActive = customGameIds != null;
    // Mobile-only: the chip filters collapse behind a toggle to keep the phone
    // layout tidy. Has no effect at md+ (the toggle is hidden, chips always show).
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // Historical stats fetched from the prop-tool API for the selected player.
    const [statsData, setStatsData] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    // Over/under line that drives bar colours + hit-rate. Auto-suggested when the
    // player or market changes; user edits persist until then.
    const [line, setLine] = useState('');

    // Most recent season on record for the loaded player (the default selection).
    const latestSeason = useMemo(() => {
        const seasons = statsData?.seasons_returned || [];
        return seasons.length ? Math.max(...seasons) : null;
    }, [statsData]);

    const resetFilters = useCallback(() => {
        setTimeframe(null);
        setVenue('all');
        setOpponent('all');
        setWithoutPlayers([]);
        setWithPlayers([]);
        setCustomGameIds(null);
        setCustomSettings(null);
        setSelectedSeasons(latestSeason != null ? [latestSeason] : []);
    }, [latestSeason]);

    // Leaving the custom dataset returns control to the standard filters and
    // discards the saved configuration.
    const clearCustom = useCallback(() => {
        setCustomGameIds(null);
        setCustomSettings(null);
    }, []);

    // Dataset chips toggle a recency window. Selecting one re-syncs the season
    // chip to the seasons those games span; clicking the active one clears the
    // window and falls back to the default (latest) season.
    const handleTimeframeChange = useCallback((tf) => {
        setCustomGameIds(null);
        if (timeframe === tf) {
            setTimeframe(null);
            setSelectedSeasons(latestSeason != null ? [latestSeason] : []);
        } else {
            setTimeframe(tf);
            setSelectedSeasons(seasonsInWindow(statsData?.stats, tf));
        }
    }, [timeframe, statsData, latestSeason]);

    const inputRef = useRef(null);
    const searchWrapRef = useRef(null);

    // Focus the player search when the page mounts — but not when we arrive via
    // a deep link (a player is pre-selected), so the results dropdown stays closed.
    useEffect(() => {
        if (location.state?.playerName) return;
        const t = setTimeout(() => inputRef.current?.focus(), 80);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close the search dropdown when clicking outside of it.
    useEffect(() => {
        if (!isSearchOpen) return;
        const handler = (e) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
                setIsSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isSearchOpen]);

    // Fetch the player's full history (all seasons) once a player is selected.
    // Season/venue/opponent/timeframe are then applied client-side so toggling
    // filters is instant and doesn't burn through the rate limit.
    useEffect(() => {
        if (!selectedPlayer?.player_id) {
            setStatsData(null);
            setStatsError(null);
            setStatsLoading(false);
            return;
        }
        let active = true;
        setStatsLoading(true);
        setStatsError(null);
        const withoutIds = withoutPlayers.map((p) => p.player_id);
        const withIds = withPlayers.map((p) => p.player_id);
        propToolService.getAflPlayerStats(selectedPlayer.player_id, undefined, withoutIds, withIds).then((res) => {
            if (!active) return;
            if (res.success) {
                setStatsData(res.data);
                setStatsError(null);
            } else {
                setStatsData(null);
                setStatsError(res);
            }
            setStatsLoading(false);
        });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPlayer?.player_id, reloadKey, withoutPlayers.map((p) => p.player_id).join(','), withPlayers.map((p) => p.player_id).join(',')]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        const matches = [];
        for (const p of AFL_PLAYERS) {
            if (p.player_name?.toLowerCase().includes(q) || p.team?.toLowerCase().includes(q)) {
                matches.push(p);
                if (matches.length >= MAX_RESULTS) break;
            }
        }
        return matches;
    }, [query]);

    useEffect(() => { setHighlightIndex(0); }, [query]);

    const handleSelectPlayer = useCallback((player) => {
        setSelectedPlayer(player);
        setQuery(player.player_name);
        setIsSearchOpen(false);
        resetFilters();
    }, [resetFilters]);

    const handleClearPlayer = useCallback(() => {
        setSelectedPlayer(null);
        setQuery('');
        setIsSearchOpen(false);
        inputRef.current?.focus();
    }, []);

    // Deep-link support: when navigated here with a player (e.g. from the Snipe
    // Feed), auto-select that player and pre-set the market + line. Runs once.
    // The line is stashed in a ref so the line-auto-suggest effect applies it
    // once the player's stats load (instead of overwriting it with the average).
    const deepLinkApplied = useRef(false);
    const pendingLineRef = useRef(null);
    useEffect(() => {
        if (deepLinkApplied.current) return;
        const st = location.state;
        const name = st?.playerName ? String(st.playerName).trim().toLowerCase() : '';
        if (!name) return;
        let match = AFL_PLAYERS.find((p) => p.player_name?.toLowerCase() === name);
        if (!match) match = AFL_PLAYERS.find((p) => p.player_name?.toLowerCase().includes(name));
        if (!match) return;
        deepLinkApplied.current = true;
        if (typeof st.line === 'number' && Number.isFinite(st.line)) pendingLineRef.current = st.line;
        if (st.propType && STAT_FIELD[st.propType]) setMarket(st.propType);
        handleSelectPlayer(match);
    }, [location.state, handleSelectPlayer]);

    // Once a player's history loads, default the season chip to the latest season
    // on record. Fires only when the latest season changes, so manual selections
    // and dataset-driven windows stick.
    useEffect(() => {
        if (latestSeason != null) setSelectedSeasons([latestSeason]);
    }, [latestSeason]);

    const handleKeyDown = (e) => {
        if (!isSearchOpen || results.length === 0) {
            if (e.key === 'Escape') inputRef.current?.blur();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIndex((i) => (i + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex((i) => (i - 1 + results.length) % results.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const pick = results[highlightIndex];
            if (pick) handleSelectPlayer(pick);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsSearchOpen(false);
        }
    };

    const marketField = STAT_FIELD[market];
    const lineNum = line === '' ? null : Number(line);
    const validLine = lineNum != null && !Number.isNaN(lineNum) ? lineNum : null;

    // Seasons on record for this player, most recent first.
    const seasonList = useMemo(
        () => (statsData?.seasons_returned || []).slice().sort((a, b) => b - a),
        [statsData]
    );

    // Opponent options derived from the player's actual game history.
    const opponentOptions = useMemo(
        () => Array.from(new Set((statsData?.stats || []).map((s) => s.opponent).filter(Boolean))).sort(),
        [statsData]
    );

    // Specific venues the player has actually played at (abbreviations).
    const venuePlaces = useMemo(
        () => Array.from(new Set((statsData?.stats || []).map((s) => s.game?.venue).filter(Boolean))).sort(),
        [statsData]
    );

    const venueSummary = venue === 'all' ? 'All venues' : venue;

    const toggleSeason = (s) => {
        setCustomGameIds(null);
        setSelectedSeasons((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    };
    // The custom dataset takes precedence; otherwise empty/every-season both read
    // as "All seasons".
    const seasonsActive = customActive || (selectedSeasons.length > 0 && selectedSeasons.length < seasonList.length);
    const seasonSummary = customActive
        ? 'CUSTOM'
        : (selectedSeasons.length > 0 && selectedSeasons.length < seasonList.length
            ? selectedSeasons.slice().sort((a, b) => b - a).join(', ')
            : 'All seasons');

    // Same-team teammates for the with/without filters (excludes the selected player).
    const teammates = useMemo(() => {
        const team = (selectedPlayer?.team || statsData?.player?.team || '').toUpperCase();
        const selfId = selectedPlayer?.player_id;
        if (!team) return [];
        return AFL_PLAYERS
            .filter((p) => (p.team || '').toUpperCase() === team && p.player_id !== selfId)
            .sort((a, b) => a.player_name.localeCompare(b.player_name));
    }, [selectedPlayer, statsData]);

    // A player can't be in both selectors (it would be a contradiction the API
    // resolves to 0 games), so each menu hides players chosen in the other.
    const withIdSet = useMemo(() => new Set(withPlayers.map((p) => p.player_id)), [withPlayers]);
    const withoutIdSet = useMemo(() => new Set(withoutPlayers.map((p) => p.player_id)), [withoutPlayers]);
    const withoutOptions = useMemo(() => teammates.filter((p) => !withIdSet.has(p.player_id)), [teammates, withIdSet]);
    const withOptions = useMemo(() => teammates.filter((p) => !withoutIdSet.has(p.player_id)), [teammates, withoutIdSet]);

    const toggleWithout = (player) => {
        // The teammate filters change the underlying game universe, so a prior
        // hand-picked dataset no longer applies.
        setCustomGameIds(null);
        setWithoutPlayers((prev) =>
            prev.some((p) => p.player_id === player.player_id)
                ? prev.filter((p) => p.player_id !== player.player_id)
                : [...prev, player]
        );
    };
    const clearWithout = () => { setCustomGameIds(null); setWithoutPlayers([]); };
    const withoutSummary = withoutPlayers.length
        ? (withoutPlayers.length === 1 ? withoutPlayers[0].player_name : `${withoutPlayers.length} players`)
        : 'Anyone';

    const toggleWith = (player) => {
        setCustomGameIds(null);
        setWithPlayers((prev) =>
            prev.some((p) => p.player_id === player.player_id)
                ? prev.filter((p) => p.player_id !== player.player_id)
                : [...prev, player]
        );
    };
    const clearWith = () => { setCustomGameIds(null); setWithPlayers([]); };
    const withSummary = withPlayers.length
        ? (withPlayers.length === 1 ? withPlayers[0].player_name : `${withPlayers.length} players`)
        : 'Anyone';

    // Stats after season / venue / opponent / timeframe filters (chronological).
    // A confirmed custom dataset overrides everything: it's just the chosen games
    // (kept in chronological order) intersected with whatever's currently loaded.
    const filteredGames = useMemo(() => {
        const all = statsData?.stats ? statsData.stats.slice() : [];
        if (customGameIds != null) {
            const ids = new Set(customGameIds);
            return all.filter((s) => ids.has(s.id ?? s.match_id));
        }
        let g = all;
        if (selectedSeasons.length) g = g.filter((s) => selectedSeasons.includes(s.season));
        if (venue !== 'all') g = g.filter((s) => s.game?.venue === venue);
        if (opponent !== 'all') g = g.filter((s) => s.opponent === opponent);
        if (timeframe === 'l5') g = g.slice(-5);
        else if (timeframe === 'l10') g = g.slice(-10);
        else if (timeframe === 'l20') g = g.slice(-20);
        return g;
    }, [statsData, customGameIds, selectedSeasons, venue, opponent, timeframe]);

    // Per-game series for the selected market (chart + game log).
    const series = useMemo(
        () => filteredGames.map((s) => ({
            key: s.id ?? s.match_id,
            value: num(s[marketField]),
            behinds: num(s.behinds),
            roundShort: roundShort(s),
            roundDesc: s.game?.round_desc || roundShort(s),
            opponent: s.opponent,
            opponentFull: resolveOpponentFull(s),
            isHome: designatedSide(s) === 'home',
            venue: s.game?.venue || '',
            season: s.season,
            dateLabel: formatGameDate(s.game?.start_time),
            stat: s,
        })),
        [filteredGames, marketField]
    );

    // Per-market averages across the filtered set (drives the stat cards).
    const averagesByMarket = useMemo(() => {
        const out = {};
        for (const m of MARKET_OPTIONS) {
            const field = STAT_FIELD[m.value];
            out[m.value] = average(filteredGames.map((s) => num(s[field])));
        }
        return out;
    }, [filteredGames]);

    // Auto-suggest a line whenever the player or market changes (full-history
    // average rounded to the nearest .5). User edits persist until then.
    useEffect(() => {
        // A deep-linked line (from the Snipe Feed) wins — but only apply it once
        // the player's stats have loaded, so the post-load default can't clobber
        // it. Until then, hold off on computing the average default.
        if (pendingLineRef.current != null) {
            if (statsData?.stats) {
                setLine(String(pendingLineRef.current));
                pendingLineRef.current = null;
            }
            return;
        }
        const stats = statsData?.stats || [];
        const field = STAT_FIELD[market];
        setLine(defaultLineFor(average(stats.map((s) => num(s[field])))));
    }, [statsData, market]);

    const identity = statsData?.player
        ? {
            player_id: statsData.player.player_id,
            player_name: statsData.player.player_name,
            team: statsData.player.team,
            image_src: statsData.player.image_src,
        }
        : selectedPlayer;

    const activeMarketLabel = MARKET_OPTIONS.find((m) => m.value === market)?.label || '';
    const marketValues = series.map((g) => g.value).filter((v) => v != null);
    const marketAvg = averagesByMarket[market];
    const sampleGames = marketValues.length;
    const hasData = statsData && (statsData.stats || []).length > 0;

    // Count of active chip filters — drives the badge on the mobile toggle only.
    const activeFilterCount = [
        timeframe != null,
        venue !== 'all',
        selectedSeasons.length > 0,
        opponent !== 'all',
        withPlayers.length > 0,
        withoutPlayers.length > 0,
        customActive,
    ].filter(Boolean).length;

    return (
        <div className="p-2 md:p-4 min-h-[calc(100dvh-72px)] md:min-h-[100dvh] flex flex-col">
            <div className="w-full mx-auto animate-fade-in flex-1 flex flex-col">
                {/* Main card */}
                <div className="relative rounded-2xl border border-slate-700/50 bg-slate-900/40 overflow-hidden flex-1 flex flex-col">
                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" />

                {/* Filters — player search + data transforms, single row */}
                <div className="relative px-5 md:px-6 py-4 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2.5 mb-3">
                        <button
                            type="button"
                            onClick={() => navigate(backTo)}
                            aria-label="Go back"
                            className="flex items-center gap-1.5 -ml-1 pl-1.5 pr-2 py-1 rounded-lg text-[10px] font-mono font-medium text-slate-400 hover:text-white hover:bg-white/[0.06] transition-all whitespace-nowrap"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                        <span className="w-px h-3.5 bg-slate-700/60" />
                        <svg className="w-3.5 h-3.5 text-emerald-400/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 018 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                        </svg>
                        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300/90">Filters</span>
                        <span className="h-px flex-1 bg-slate-800/70" />
                        <button
                            type="button"
                            onClick={resetFilters}
                            className="text-[10px] font-mono text-slate-500 hover:text-emerald-300 transition-colors whitespace-nowrap"
                        >
                            Reset
                        </button>
                    </div>
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6">
                        {/* Player search + mobile filters toggle share one row on
                            phones (75/25). `md:contents` dissolves this wrapper at
                            md+ so the desktop/tablet layout is unchanged. */}
                        <div className="flex items-end gap-2 md:contents">
                        {/* Player search */}
                        <div ref={searchWrapRef} className="relative w-[calc(90%_-_0.25rem)] md:w-full lg:w-64 flex-shrink-0">
                            <label className="block text-[10px] font-mono uppercase tracking-[0.16em] text-slate-500 mb-1.5">Player</label>
                            <div className={`relative flex items-center gap-2.5 rounded-lg border bg-slate-900/70 pl-2 pr-2.5 transition-colors ${
                                isSearchOpen ? 'border-emerald-500/50' : 'border-slate-700/70 hover:border-slate-600'
                            }`}>
                                {selectedPlayer ? (
                                    <PlayerAvatar player={selectedPlayer} size={28} />
                                ) : (
                                    <svg className="w-4 h-4 ml-1.5 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                )}
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        setIsSearchOpen(true);
                                        if (selectedPlayer) setSelectedPlayer(null);
                                    }}
                                    onFocus={() => setIsSearchOpen(true)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Search player or team..."
                                    className="flex-1 min-w-0 bg-transparent py-2 text-sm text-white placeholder-slate-600 focus:outline-none"
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                {(query || selectedPlayer) && (
                                    <button
                                        onClick={handleClearPlayer}
                                        aria-label="Clear"
                                        className="p-1 rounded text-slate-500 hover:text-white hover:bg-white/[0.06] transition-all flex-shrink-0"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>

                            {/* Autocomplete dropdown */}
                            {isSearchOpen && query.trim() && (
                                <div className="absolute z-50 left-0 right-0 mt-2 rounded-xl border border-slate-700/70 bg-slate-900 shadow-2xl shadow-black/60 overflow-hidden">
                                    {results.length > 0 ? (
                                        <ul className="max-h-[320px] overflow-y-auto styled-scrollbar py-1">
                                            {results.map((p, i) => (
                                                <li key={p.player_id}>
                                                    <button
                                                        type="button"
                                                        onMouseEnter={() => setHighlightIndex(i)}
                                                        onClick={() => handleSelectPlayer(p)}
                                                        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                                            i === highlightIndex ? 'bg-emerald-500/10' : 'hover:bg-slate-800/60'
                                                        }`}
                                                    >
                                                        <PlayerAvatar player={p} size={34} />
                                                        <div className="min-w-0 flex-1">
                                                            <div className="text-sm font-medium text-white truncate">{p.player_name}</div>
                                                            <div className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">{p.team}</div>
                                                        </div>
                                                        {i === highlightIndex && (
                                                            <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="px-3 py-4 text-center text-xs font-mono text-slate-500">
                                            No players match &ldquo;{query.trim()}&rdquo;
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Mobile-only toggle that collapses the chip filters. Hidden
                            at md+, so the desktop/tablet layout is unchanged. */}
                        <button
                            type="button"
                            onClick={() => setMobileFiltersOpen((v) => !v)}
                            aria-expanded={mobileFiltersOpen}
                            aria-label="Toggle filters"
                            className={`md:hidden w-[calc(10%_-_0.25rem)] flex-shrink-0 flex items-center justify-center px-1 py-2 rounded-lg border transition-colors ${
                                mobileFiltersOpen
                                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                    : 'border-slate-700/60 bg-slate-900/50 text-slate-300'
                            }`}
                        >
                            <span className="relative inline-flex">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M6 12h12M10 17h4" />
                                </svg>
                                {activeFilterCount > 0 && (
                                    <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 px-1 rounded-full bg-emerald-500 text-emerald-950 text-[8px] font-mono font-bold leading-none inline-flex items-center justify-center tabular-nums">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </span>
                        </button>
                        </div>

                        {/* Chip filters — paired two-up rows on mobile (inside the
                            collapsible), restored to the original wrap row at md+.
                            Mobile pairing is driven purely by `order-*` so the DOM
                            order (and thus the md+ layout) is never altered. */}
                        <div className={`${mobileFiltersOpen ? 'grid' : 'hidden'} grid-cols-2 gap-3 md:flex md:flex-row md:flex-wrap md:items-start md:gap-x-5 md:gap-y-3`}>
                            <FilterField label="Dataset" className="order-1 md:order-none">
                                <Segmented options={TIMEFRAME_OPTIONS} value={timeframe} onChange={handleTimeframeChange} />
                            </FilterField>

                            <FilterField label="Venue" className="order-3 md:order-none">
                                <ChipDropdown summary={venueSummary} active={venue !== 'all'} panelWidth="w-48">
                                    {({ close }) => (
                                        <div className="py-1 max-h-[260px] overflow-y-auto styled-scrollbar">
                                            <ChipMenuItem
                                                label="All venues"
                                                checked={venue === 'all'}
                                                onClick={() => { setCustomGameIds(null); setVenue('all'); close(); }}
                                            />
                                            {venuePlaces.length > 0 && <div className="my-1 h-px bg-slate-800" />}
                                            {venuePlaces.map((v) => (
                                                <ChipMenuItem
                                                    key={v}
                                                    label={v}
                                                    checked={venue === v}
                                                    onClick={() => { setCustomGameIds(null); setVenue(v); close(); }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </ChipDropdown>
                            </FilterField>

                            <FilterField label="Season" className="order-2 md:order-none">
                                <ChipDropdown summary={seasonSummary} active={seasonsActive} panelWidth="w-44">
                                    {() => (
                                        <div className="py-1 max-h-[260px] overflow-y-auto styled-scrollbar">
                                            <ChipMenuItem
                                                label="All seasons"
                                                checked={!seasonsActive}
                                                multi
                                                onClick={() => { setCustomGameIds(null); setSelectedSeasons([]); }}
                                            />
                                            {seasonList.length > 0 && <div className="my-1 h-px bg-slate-800" />}
                                            {seasonList.map((s) => (
                                                <ChipMenuItem
                                                    key={s}
                                                    label={String(s)}
                                                    checked={selectedSeasons.includes(s)}
                                                    multi
                                                    onClick={() => toggleSeason(s)}
                                                />
                                            ))}
                                            {!seasonList.length && (
                                                <div className="px-3 py-2 text-[11px] font-mono text-slate-600">No seasons on record</div>
                                            )}
                                        </div>
                                    )}
                                </ChipDropdown>
                            </FilterField>

                            <FilterField label="Opponent" className="order-4 md:order-none">
                                <ChipDropdown
                                    summary={opponent === 'all' ? 'All opponents' : opponent}
                                    active={opponent !== 'all'}
                                    panelWidth="w-48"
                                >
                                    {({ close }) => (
                                        <div className="py-1 max-h-[260px] overflow-y-auto styled-scrollbar">
                                            <ChipMenuItem
                                                label="All opponents"
                                                checked={opponent === 'all'}
                                                onClick={() => { setCustomGameIds(null); setOpponent('all'); close(); }}
                                            />
                                            {opponentOptions.map((t) => (
                                                <ChipMenuItem
                                                    key={t}
                                                    label={t}
                                                    checked={opponent === t}
                                                    onClick={() => { setCustomGameIds(null); setOpponent(t); close(); }}
                                                />
                                            ))}
                                            {!opponentOptions.length && (
                                                <div className="px-3 py-2 text-[11px] font-mono text-slate-600">No opponents on record</div>
                                            )}
                                        </div>
                                    )}
                                </ChipDropdown>
                            </FilterField>

                            <FilterField label="With Player(s)" className="order-5 md:order-none">
                                <TeammateFilter
                                    teammates={withOptions}
                                    selected={withPlayers}
                                    onToggle={toggleWith}
                                    summary={withSummary}
                                    active={withPlayers.length > 0}
                                    accent="sky"
                                />
                            </FilterField>

                            <FilterField label="Without Player(s)" className="order-6 md:order-none">
                                <TeammateFilter
                                    teammates={withoutOptions}
                                    selected={withoutPlayers}
                                    onToggle={toggleWithout}
                                    summary={withoutSummary}
                                    active={withoutPlayers.length > 0}
                                    accent="amber"
                                />
                            </FilterField>

                            <FilterField label="Advanced" className="order-7 col-span-2 md:order-none">
                                <div className="flex items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setShowCustomModal(true)}
                                        disabled={!hasData}
                                        className={`flex-1 md:flex-none flex items-center justify-center md:justify-start gap-1.5 px-2.5 py-2 rounded-lg text-[11px] font-mono font-medium border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                                            customActive
                                                ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                                : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                                        }`}
                                    >
                                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M6 12h12M10 17h4" />
                                        </svg>
                                        <span className="truncate md:max-w-[150px]">
                                            {customActive ? `Custom · ${customGameIds.length}` : 'Custom Filter'}
                                        </span>
                                    </button>
                                    {customActive && (
                                        <button
                                            type="button"
                                            onClick={clearCustom}
                                            aria-label="Clear custom dataset"
                                            title="Clear custom dataset"
                                            className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/[0.06] border border-slate-700/60 transition-all"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </FilterField>
                        </div>
                    </div>

                    {/* Active "with" filter banner */}
                    {withPlayers.length > 0 && (
                            <div className="mt-3 flex items-center flex-wrap gap-2 rounded-lg border border-sky-500/30 bg-sky-500/[0.07] px-3 py-2">
                                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-sky-300/90">With</span>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {withPlayers.map((p) => (
                                        <span key={p.player_id} className="inline-flex items-center gap-1.5 rounded-md bg-sky-500/10 border border-sky-500/25 pl-1 pr-2 py-0.5">
                                            <PlayerAvatar player={p} size={16} ring={false} />
                                            <span className="text-[11px] font-mono text-sky-100/90">{p.player_name}</span>
                                            <button
                                                type="button"
                                                onClick={() => toggleWith(p)}
                                                aria-label={`Remove ${p.player_name}`}
                                                className="text-sky-300/70 hover:text-white transition-colors"
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                <span className="text-[10px] font-mono text-sky-200/60">also played</span>
                                <button
                                    type="button"
                                    onClick={clearWith}
                                    className="ml-auto text-[10px] font-mono text-sky-300/80 hover:text-white transition-colors whitespace-nowrap"
                                >
                                    Clear
                                </button>
                            </div>
                        )}

                    {/* Active "without" filter banner */}
                    {withoutPlayers.length > 0 && (
                            <div className="mt-3 flex items-center flex-wrap gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2">
                                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-amber-300/90">Without</span>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {withoutPlayers.map((p) => (
                                        <span key={p.player_id} className="inline-flex items-center gap-1.5 rounded-md bg-amber-500/10 border border-amber-500/25 pl-1 pr-2 py-0.5">
                                            <PlayerAvatar player={p} size={16} ring={false} />
                                            <span className="text-[11px] font-mono text-amber-100/90">{p.player_name}</span>
                                            <button
                                                type="button"
                                                onClick={() => toggleWithout(p)}
                                                aria-label={`Remove ${p.player_name}`}
                                                className="text-amber-300/70 hover:text-white transition-colors"
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                <span className="text-[10px] font-mono text-amber-200/60">didn&rsquo;t play</span>
                                <button
                                    type="button"
                                    onClick={clearWithout}
                                    className="ml-auto text-[10px] font-mono text-amber-300/80 hover:text-white transition-colors whitespace-nowrap"
                                >
                                    Clear
                                </button>
                            </div>
                        )}
                </div>

                {/* Body */}
                <div className="relative px-2 md:px-6 py-6 flex-1 flex flex-col">
                    {selectedPlayer ? (
                        <div className="animate-fade-in">
                            {/* Player box — identity inline with stat averages */}
                            <div className="rounded-2xl border border-slate-700/50 bg-slate-900/50 p-3 md:p-4">
                                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                    {/* Identity */}
                                    <div className="flex items-center gap-2 lg:gap-3.5 lg:w-52 flex-shrink-0">
                                        <PlayerAvatar player={identity} size={56} />
                                        <div className="min-w-0 flex-1 lg:flex-none">
                                            <div className="text-left text-base font-bold text-white tracking-tight truncate">{identity.player_name}</div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{identity.team}</span>
                                                <span className="text-slate-700">·</span>
                                                <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-mono font-semibold text-emerald-300 uppercase tracking-wider">
                                                    {activeMarketLabel}
                                                </span>
                                            </div>
                                            <div className="hidden lg:block text-[10px] font-mono text-slate-600 mt-1">
                                                <span className="text-slate-400 font-semibold">{statsLoading ? '—' : sampleGames}</span> games
                                            </div>
                                        </div>
                                        {/* Mobile-only: games count pinned right to balance the row
                                            and use the trailing whitespace. Hidden at lg+. */}
                                        <div className="lg:hidden flex-shrink-0 text-center pl-3">
                                            <div className="text-xl font-mono font-bold text-white tabular-nums leading-none">{statsLoading ? '—' : sampleGames}</div>
                                            <div className="text-[9px] font-mono text-slate-500 uppercase tracking-wider mt-1">games</div>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="hidden lg:block w-px self-stretch bg-slate-700/40 flex-shrink-0" />

                                    {/* Stat averages per category */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-500">Stat Averages</span>
                                            <span className="text-[10px] font-mono text-slate-600">— tap a stat to change the market</span>
                                        </div>
                                        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                                        {MARKET_OPTIONS.map((m) => {
                                            const active = m.value === market;
                                            const avg = averagesByMarket[m.value];
                                            const val = statsLoading ? '·' : (avg == null ? '—' : avg.toFixed(1));
                                            return (
                                                <button
                                                    key={m.value}
                                                    type="button"
                                                    onClick={() => setMarket(m.value)}
                                                    className={`text-left rounded-xl border px-2 py-1.5 md:px-3 md:py-2 transition-all ${
                                                        active
                                                            ? 'bg-emerald-500/10 border-emerald-500/40'
                                                            : 'bg-slate-950/40 border-slate-800/70 hover:border-slate-700'
                                                    }`}
                                                >
                                                    <div className={`text-[9px] md:text-[10px] font-mono uppercase tracking-wider mb-0.5 truncate ${active ? 'text-emerald-400/80' : 'text-slate-500'}`}>
                                                        {m.label}
                                                    </div>
                                                    <div className={`text-sm md:text-lg font-mono font-bold tabular-nums leading-none ${active ? 'text-emerald-300' : 'text-white'}`}>
                                                        {val}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Analysis: loading / error / empty / charts + game log */}
                            <div className="mt-4">
                                {statsLoading ? (
                                    <PropLoadingState />
                                ) : statsError ? (
                                    <PropErrorState error={statsError} onRetry={() => setReloadKey((k) => k + 1)} />
                                ) : !hasData ? (
                                    <PropEmptyState
                                        title="No historical data"
                                        message={`We don't have any recorded games for ${identity.player_name} yet.`}
                                    />
                                ) : series.length === 0 ? (
                                    <PropEmptyState
                                        title="No games match these filters"
                                        message="Try widening the timeframe, season, venue or opponent filters."
                                    />
                                ) : (
                                    <PropAnalysis
                                        playerName={identity.player_name}
                                        marketKey={market}
                                        marketLabel={activeMarketLabel}
                                        series={series}
                                        marketAvg={marketAvg}
                                        line={line}
                                        validLine={validLine}
                                        onLineChange={setLine}
                                        canTip={canTip}
                                        preferredBookmaker={deepLinkBook}
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center py-16 px-6">
                            <div className="w-14 h-14 rounded-2xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center mb-5">
                                <svg className="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <h4 className="text-base font-semibold text-white mb-2">Search for a player to begin</h4>
                            <p className="text-sm font-mono text-slate-500 leading-relaxed max-w-sm">
                                Find any AFL player, then pick a market to dive into their historical prop performance.
                            </p>
                        </div>
                    )}
                </div>
                </div>
            </div>

            {showCustomModal && (
                <CustomPropDataFilterModal
                    stats={statsData?.stats || []}
                    player={identity}
                    seasonList={seasonList}
                    initialSelectedIds={customGameIds}
                    initialSettings={customSettings}
                    onConfirm={(ids, settings) => {
                        setCustomGameIds(ids);
                        setCustomSettings(settings);
                        setShowCustomModal(false);
                    }}
                    onClose={() => setShowCustomModal(false)}
                />
            )}
        </div>
    );
}

export default AFLPropTool;
